import React, { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatTick } from '../utils/formatTick';
import {
  UserInsightsRange,
  UserInsightsResponse,
  UserInsightsSeriesPoint,
} from '../api/useUserInsights';

type ViewMode = 'cumulative' | 'newSignups';
type RangeOption = { key: UserInsightsRange; label: string };
type ViewOption = { key: ViewMode; label: string };

const RANGE_OPTIONS: RangeOption[] = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'Week' },
  { key: 'month', label: 'Month' },
];

const VIEW_OPTIONS: ViewOption[] = [
  { key: 'cumulative', label: 'Cumulative' },
  { key: 'newSignups', label: 'New Signups' },
];

interface ChartTooltipProps {
  active?: boolean;
  payload?: Array<{ payload?: UserInsightsSeriesPoint }>;
  range: UserInsightsRange;
  view: ViewMode;
}

function ChartTooltip({ active, payload, range, view }: ChartTooltipProps): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;
  const p = payload[0]?.payload;
  if (!p || typeof p.ts !== 'number') return null;
  const label = view === 'cumulative' ? 'Total' : 'New';
  return (
    <div className="bg-gray-900/95 border border-gray-700 rounded-lg px-3 py-2 text-xs shadow-xl backdrop-blur-sm">
      <div className="text-gray-400 mb-1">{formatTick(p.ts, range)}</div>
      <div className="text-white font-semibold">{label}: {p.count.toLocaleString()}</div>
    </div>
  );
}

export interface UserGrowthChartProps {
  data: UserInsightsResponse | null;
  loading: boolean;
  error: string | null;
  range: UserInsightsRange;
  onRangeChange: (range: UserInsightsRange) => void;
  className?: string;
}

export function UserGrowthChart({
  data,
  loading,
  error,
  range,
  onRangeChange,
  className,
}: UserGrowthChartProps): React.ReactElement {
  const [view, setView] = useState<ViewMode>('cumulative');

  const points: UserInsightsSeriesPoint[] = useMemo(() => {
    if (!data) return [];
    return view === 'cumulative' ? data.cumulative : data.newSignups;
  }, [data, view]);

  const xTickFormatter = useMemo(
    () => (value: number) => formatTick(value, range),
    [range],
  );

  const tickCount = points.length === 0
    ? 0
    : Math.min(6, Math.max(2, Math.floor(points.length / 4)));

  const isEmpty = points.length === 0 || points.every((p) => p.count === 0);

  return (
    <div className={`bg-gray-900/60 border border-gray-800 rounded-2xl p-5 ${className ?? ''}`}>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-white font-semibold text-base">User Growth</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Registered users from the <code>users</code> table. Refreshes every 5 minutes.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 self-start sm:self-auto">
          <div className="flex items-center gap-1 bg-gray-950/60 border border-gray-800 rounded-full p-1">
            {VIEW_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setView(opt.key)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  view === opt.key
                    ? 'bg-emerald-700 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-gray-950/60 border border-gray-800 rounded-full p-1">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt.key}
                type="button"
                onClick={() => onRangeChange(opt.key)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  range === opt.key
                    ? 'bg-emerald-700 text-white'
                    : 'text-gray-300 hover:text-white hover:bg-gray-800'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="h-64 w-full">
        {error && (
          <div className="h-full flex items-center justify-center text-sm text-red-400">
            {error}
          </div>
        )}
        {!error && isEmpty && (
          <div className="h-full flex items-center justify-center text-sm text-gray-500">
            {loading ? 'Loading…' : 'No signups in this range yet.'}
          </div>
        )}
        {!error && !isEmpty && (
          <ResponsiveContainer width="100%" height="100%">
            {view === 'cumulative' ? (
              <AreaChart data={points} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <defs>
                  <linearGradient id="growthFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10b981" stopOpacity={0.6} />
                    <stop offset="100%" stopColor="#10b981" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                <XAxis
                  dataKey="ts"
                  type="number"
                  domain={['dataMin', 'dataMax']}
                  tickFormatter={xTickFormatter}
                  stroke="#6b7280"
                  fontSize={11}
                  tickCount={tickCount}
                  minTickGap={32}
                />
                <YAxis allowDecimals={false} stroke="#6b7280" fontSize={11} width={48} />
                <Tooltip content={<ChartTooltip range={range} view={view} />} />
                <Area
                  type="monotone"
                  dataKey="count"
                  stroke="#34d399"
                  strokeWidth={2}
                  fill="url(#growthFill)"
                  isAnimationActive={false}
                />
              </AreaChart>
            ) : (
              <BarChart data={points} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" vertical={false} />
                <XAxis
                  dataKey="ts"
                  type="category"
                  tickFormatter={xTickFormatter}
                  stroke="#6b7280"
                  fontSize={11}
                  tickCount={tickCount}
                  minTickGap={16}
                />
                <YAxis allowDecimals={false} stroke="#6b7280" fontSize={11} width={40} />
                <Tooltip content={<ChartTooltip range={range} view={view} />} cursor={{ fill: '#10b98122' }} />
                <Bar dataKey="count" fill="#34d399" isAnimationActive={false} />
              </BarChart>
            )}
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

export default UserGrowthChart;
